/**
 * The catalog built from `outcomeMetaUpdates` events (#1809, WS_DESIGN_1809 section 6). Pure: no React, no timers, so Android
 * and iOS can follow it line by line against shared/fixtures/outcome-meta-events.json.
 *
 *   outcomeCreated    insert or replace the outcome. The row is kept as sent: `venue` and `deployerFeeScale` stay absent when
 *                     absent, as on a snapshot row, and a replaced row keeps nothing of the old one.
 *   outcomeSettled    remove the outcome
 *   questionUpdated   insert or replace the whole question (a new question looks like this; there is no questionCreated)
 *   questionSettled   remove the question
 *
 * A frame is one atomic update: a question passes through intermediate states inside a frame, and those are never published.
 *
 * Every REST snapshot gets the last minute of frames replayed over it, including frames from before the response arrived:
 * REST was seen lacking an outcome whose event came 1,396 ms before the response. Replay is safe to repeat (last write wins).
 * The stream has no sequence numbers and `deployers` and `feeScale` are in no event, so a slow read compares and corrects.
 */
import { canonicalJson } from '../hl/canonical';
import type { OutcomeMetaResponse, OutcomeMetaUpdate, OutcomeSpec, QuestionSpec } from '../hl/types';

/** How far back frames are replayed over a snapshot. */
export const REPLAY_MS = 60_000;
/** Settled rows kept for names, oldest out first. */
const RETIRED_MAX = 500;

type Meta = OutcomeMetaResponse;

const KNOWN_KINDS = ['outcomeCreated', 'outcomeSettled', 'questionUpdated', 'questionSettled'];

export interface FrameResult {
  meta: Meta;
  /** Event kinds this code cannot apply, malformed known ones as `<kind>?`. */
  unknown: string[];
  /** Rows the frame inserted or replaced, in event order. */
  outcomes: OutcomeSpec[];
  questions: QuestionSpec[];
  removedOutcomes: OutcomeSpec[];
  removedQuestions: QuestionSpec[];
}

const isRow = (v: unknown, field: string): boolean =>
  v !== null && typeof v === 'object' && typeof (v as Record<string, unknown>)[field] === 'number';

/** Applies every event of a frame and returns one new catalog. Returns `meta` itself when nothing changed. */
export function applyFrame(meta: Meta, events: readonly OutcomeMetaUpdate[]): FrameResult {
  const outcomes = new Map((meta.outcomes ?? []).map((o) => [o.outcome, o]));
  const questions = new Map((meta.questions ?? []).map((q) => [q.question, q]));
  const r: FrameResult = { meta, unknown: [], outcomes: [], questions: [], removedOutcomes: [], removedQuestions: [] };
  let changed = false;
  for (const ev of events) {
    for (const [kind, val] of Object.entries((ev ?? {}) as Record<string, unknown>)) {
      if (kind === 'outcomeCreated' && isRow(val, 'outcome')) {
        const row = val as OutcomeSpec;
        outcomes.set(row.outcome, row);
        r.outcomes.push(row);
        changed = true;
      } else if (kind === 'questionUpdated' && isRow(val, 'question')) {
        const row = val as QuestionSpec;
        questions.set(row.question, row);
        r.questions.push(row);
        changed = true;
      } else if (kind === 'outcomeSettled' && typeof val === 'number') {
        const row = outcomes.get(val);
        if (row) {
          outcomes.delete(val);
          r.removedOutcomes.push(row);
          changed = true;
        }
      } else if (kind === 'questionSettled' && typeof val === 'number') {
        const row = questions.get(val);
        if (row) {
          questions.delete(val);
          r.removedQuestions.push(row);
          changed = true;
        }
      } else {
        r.unknown.push(KNOWN_KINDS.includes(kind) ? `${kind}?` : kind);
      }
    }
  }
  if (changed) r.meta = { ...meta, outcomes: [...outcomes.values()], questions: [...questions.values()] };
  return r;
}

export interface CatalogDiff {
  /** Ids as `o<outcome>` / `q<question>`, each list sorted. Listed by the snapshot, not held. */
  missing: string[];
  /** Held, not listed by the snapshot. */
  extra: string[];
  changed: string[];
}

function diffSide<T>(held: readonly T[], rows: readonly T[], id: (r: T) => number, prefix: string, out: CatalogDiff): void {
  const have = new Map(held.map((r) => [id(r), r]));
  const fresh = new Map(rows.map((r) => [id(r), r]));
  for (const [k, row] of fresh) {
    if (!have.has(k)) out.missing.push(`${prefix}${k}`);
    else if (canonicalJson(have.get(k)) !== canonicalJson(row)) out.changed.push(`${prefix}${k}`);
  }
  for (const k of have.keys()) if (!fresh.has(k)) out.extra.push(`${prefix}${k}`);
}

/** Where the held catalog differs from a snapshot, by id: both come in any order. */
export function diffCatalog(held: Meta, snapshot: Meta): CatalogDiff {
  const out: CatalogDiff = { missing: [], extra: [], changed: [] };
  diffSide(held.outcomes ?? [], snapshot.outcomes ?? [], (o) => o.outcome, 'o', out);
  diffSide(held.questions ?? [], snapshot.questions ?? [], (q) => q.question, 'q', out);
  for (const list of [out.missing, out.extra, out.changed]) list.sort();
  return out;
}

export const driftCount = (d: CatalogDiff): number => d.missing.length + d.extra.length + d.changed.length;

/**
 * Outcomes a snapshot still lists although one of its questions names them as settled. Section 6's open question: no snapshot
 * has shown this yet. If it happens, such an outcome shows as drift once and the snapshot's row is taken. Question cards
 * already hide these (catalog/group.ts), so it costs a count, not a break.
 */
export function settledButListed(snapshot: Meta): number[] {
  const listed = new Set((snapshot.outcomes ?? []).map((o) => o.outcome));
  const ids = (snapshot.questions ?? []).flatMap((q) => q.settledNamedOutcomes ?? []);
  return [...new Set(ids)].filter((id) => listed.has(id)).sort((a, b) => a - b);
}

/** Why a snapshot was read. Differences after a reconnect are expected (frames lost with the socket), after a check they are not. */
export type ReadReason = 'first' | 'check' | 'reconnect';

export interface CatalogStats {
  frames: number;
  reads: number;
  /** Check reads that differed: the drift the soak looks for. */
  drifts: number;
  /** Reconnect reads that differed. */
  reconnectDiffs: number;
  /** The latest difference, ids included. */
  lastDrift: (CatalogDiff & { reason: ReadReason; at: number }) | null;
  /** Reads that failed with a catalog held (the held catalog stays). */
  failedReads: number;
  unknownEvents: number;
  /** `deployers` or `feeScale` changed between reads: no event carries them. */
  topChanged: number;
  /** settledButListed of the latest read. */
  settledListed: number[];
}

export interface SnapshotResult {
  meta: Meta;
  reason: ReadReason;
  /** Null on the first read, with nothing to compare. */
  diff: CatalogDiff | null;
}

/** One network's stream state: the last minute of frames, counters, and rows that left the catalog. */
export class CatalogFeed {
  readonly stats: CatalogStats = {
    frames: 0,
    reads: 0,
    drifts: 0,
    reconnectDiffs: 0,
    lastDrift: null,
    failedReads: 0,
    unknownEvents: 0,
    topChanged: 0,
    settledListed: [],
  };
  /** Reason for the next read, unless it is the first. */
  nextReason: ReadReason = 'check';
  private frames: { t: number; events: readonly OutcomeMetaUpdate[] }[] = [];
  private retiredOutcomes = new Map<number, { row: OutcomeSpec; t: number }>();
  private retiredQuestions = new Map<number, QuestionSpec>();

  private retireOutcome(row: OutcomeSpec, now: number): void {
    this.retiredOutcomes.delete(row.outcome);
    this.retiredOutcomes.set(row.outcome, { row, t: now });
    if (this.retiredOutcomes.size > RETIRED_MAX) this.retiredOutcomes.delete(this.retiredOutcomes.keys().next().value!);
  }

  private retireQuestion(row: QuestionSpec): void {
    this.retiredQuestions.delete(row.question);
    this.retiredQuestions.set(row.question, row);
    if (this.retiredQuestions.size > RETIRED_MAX) this.retiredQuestions.delete(this.retiredQuestions.keys().next().value!);
  }

  private retire(r: FrameResult, now: number): void {
    for (const row of r.removedOutcomes) this.retireOutcome(row, now);
    for (const row of r.removedQuestions) this.retireQuestion(row);
  }

  /** Takes one frame. With no catalog yet it is only held, for the first snapshot. */
  frame(held: Meta | undefined, events: readonly OutcomeMetaUpdate[], now: number): FrameResult {
    this.frames = this.frames.filter((f) => f.t > now - REPLAY_MS);
    this.frames.push({ t: now, events });
    this.stats.frames++;
    const r = applyFrame(held ?? { outcomes: [], questions: [] }, events);
    this.stats.unknownEvents += r.unknown.length;
    if (held) this.retire(r, now);
    return r;
  }

  /** Takes a REST snapshot: replays recent frames over it, compares with what was held, and returns the catalog to use. */
  snapshot(held: Meta | undefined, snapshot: Meta, now: number): SnapshotResult {
    const reason: ReadReason = held ? this.nextReason : 'first';
    this.nextReason = 'check';
    this.stats.reads++;
    this.frames = this.frames.filter((f) => f.t > now - REPLAY_MS);
    let meta = snapshot;
    for (const f of this.frames) {
      const r = applyFrame(meta, f.events);
      this.retire(r, now);
      meta = r.meta;
    }
    this.stats.settledListed = settledButListed(snapshot);
    if (!held) return { meta, reason, diff: null };

    const diff = diffCatalog(held, meta);
    if (driftCount(diff) > 0) {
      if (reason === 'reconnect') this.stats.reconnectDiffs++;
      else this.stats.drifts++;
      this.stats.lastDrift = { ...diff, reason, at: now };
      for (const row of held.outcomes ?? []) if (diff.extra.includes(`o${row.outcome}`)) this.retireOutcome(row, now);
      for (const row of held.questions ?? []) if (diff.extra.includes(`q${row.question}`)) this.retireQuestion(row);
    }
    if (canonicalJson([held.deployers, held.feeScale]) !== canonicalJson([meta.deployers, meta.feeScale])) this.stats.topChanged++;
    return { meta, reason, diff };
  }

  /** When the outcome left the catalog in this session, if it did. */
  settledAt(outcome: number): number | undefined {
    return this.retiredOutcomes.get(outcome)?.t;
  }

  /**
   * The catalog plus rows that left it, for names only. A settlement fill or a cancel can arrive just after its outcomeSettled,
   * and a toast or a position row still needs the market's name.
   */
  withRetired(meta: Meta | undefined): Meta | undefined {
    if (!meta || (this.retiredOutcomes.size === 0 && this.retiredQuestions.size === 0)) return meta;
    const liveO = new Set((meta.outcomes ?? []).map((o) => o.outcome));
    const liveQ = new Set((meta.questions ?? []).map((q) => q.question));
    return {
      ...meta,
      outcomes: [...(meta.outcomes ?? []), ...[...this.retiredOutcomes.values()].map((r) => r.row).filter((o) => !liveO.has(o.outcome))],
      questions: [...(meta.questions ?? []), ...[...this.retiredQuestions.values()].filter((q) => !liveQ.has(q.question))],
    };
  }
}
